import axiosInstance from "../axiosClient";

// Pilot Licenses
export const getPilotLicenses = async (token) => {
  try {
    const response = await axiosInstance.get('/compliance/licenses', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPilotLicense = async (token, id) => {
  try {
    const response = await axiosInstance.get(`/compliance/licenses/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createPilotLicense = async (token, licenseData) => {
  try {
    const response = await axiosInstance.post('/compliance/licenses', licenseData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message);
  }
};

export const updatePilotLicense = async (token, id, licenseData) => {
  try {
    const response = await axiosInstance.patch(`/compliance/licenses/${id}`, licenseData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deletePilotLicense = async (token, id) => {
  try {
    const response = await axiosInstance.delete(`/compliance/licenses/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getExpiringLicenses = async (token) => {
  try {
    const response = await axiosInstance.get('/compliance/licenses/expiring', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Checklist Templates
export const getChecklistTemplates = async (token) => {
  try {
    const response = await axiosInstance.get('/compliance/checklist-templates', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createChecklistTemplate = async (token, templateData) => {
  try {
    const response = await axiosInstance.post('/compliance/checklist-templates', templateData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateChecklistTemplate = async (token, id, templateData) => {
  try {
    const response = await axiosInstance.patch(`/compliance/checklist-templates/${id}`, templateData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteChecklistTemplate = async (token, id) => {
  try {
    const response = await axiosInstance.delete(`/compliance/checklist-templates/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Checklist Instances
export const getChecklistInstances = async (token) => {
  try {
    const response = await axiosInstance.get('/compliance/checklist-instances', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const completeChecklist = async (token, instanceData) => {
  try {
    const response = await axiosInstance.post('/compliance/checklist-instances/complete', instanceData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Export Checklist to PDF
export const exportChecklistPDF = async (token, instanceId) => {
  try {
    const response = await axiosInstance.get(
      `/compliance/checklist-instances/${instanceId}/export/pdf`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob' // Important for file download
      }
    );

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'checklist.pdf';


    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    } else {
    }

    return { blob: response.data, filename };
  } catch (error) {

    // If the error response is a blob, try to read it as JSON to get the error message
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || 'Failed to export checklist to PDF');
      } catch (parseError) {
      }
    }

    throw error;
  }
};

// Clone Template
export const cloneChecklistTemplate = async (token, templateId, cloneData) => {
  try {
    const response = await axiosInstance.post(`/compliance/checklist-templates/${templateId}/clone`, cloneData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Draft Checklists
export const createDraftChecklist = async (token, draftData) => {
  try {
    const response = await axiosInstance.post('/compliance/checklist-instances/draft', draftData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDraftChecklists = async (token) => {
  try {
    const response = await axiosInstance.get('/compliance/checklist-instances/drafts', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateDraftChecklist = async (token, id, draftData) => {
  try {
    const response = await axiosInstance.patch(`/compliance/checklist-instances/${id}`, draftData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const submitChecklist = async (token, id, finalData) => {
  try {
    const response = await axiosInstance.post(`/compliance/checklist-instances/${id}/submit`, finalData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteDraftChecklist = async (token, id) => {
  try {
    const response = await axiosInstance.delete(`/compliance/checklist-instances/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Incidents
export const getIncidents = async (token, filters = {}) => {
  try {
    const response = await axiosInstance.get('/compliance/incidents', {
      headers: { Authorization: `Bearer ${token}` },
      params: filters
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getIncident = async (token, id) => {
  try {
    const response = await axiosInstance.get(`/compliance/incidents/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const reportIncident = async (token, incidentData) => {
  try {
    const response = await axiosInstance.post('/compliance/incidents', incidentData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateIncident = async (token, id, incidentData) => {
  try {
    const response = await axiosInstance.patch(`/compliance/incidents/${id}`, incidentData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateIncidentStatus = async (token, id, status) => {
  try {
    const response = await axiosInstance.patch(`/compliance/incidents/${id}/status`, status, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteIncident = async (token, id) => {
  try {
    const response = await axiosInstance.delete(`/compliance/incidents/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Analytics
export const getComplianceSummary = async (token) => {
  try {
    const response = await axiosInstance.get('/compliance/analytics/summary', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};
